import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Typography, TextField, Grid, Switch, FormControlLabel,
    LinearProgress, Breadcrumbs, Link, Stack,
    Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Home } from '@mui/icons-material';
import orderApi from '../../../../services/orderApi';
import productApi from '../../../../services/productApi';
import AnimateButton from '../../../AnimateButton';
import { BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR } from '../../../common/color';
import Swal from 'sweetalert2';

const OrderBody = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const isNew = id === 'new';
    const [formData, setFormData] = useState({
        productId: '',
        fullName: '',
        email: '',
        contactNo:'',
        location:'',
        remarks:'',
        isActive: true,
    });
    const [previewUrl, setPreviewUrl] = useState('');
    const [errors, setErrors] = useState({ productId: '',fullName:'', email: '',contactNo:'',location:'',remarks:'' });
    const [touched, setTouched] = useState({ productId: false, fullName: false,email:false,
        contactNo:false,location:false,remarks:false });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await productApi.getAll();
                setProducts(response.data);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, []);

    useEffect(() => {
        if (!isNew) {
            const fetchOrder= async () => {
                try {
                    const order = await orderApi.getById(id);
                    setFormData({
                        productId: order.productId || null,
                        fullName: order.fullName || '',
                        email: order.email || '',
                        contactNo: order.contactNo || '',
                        location: order.location || '',
                        remarks: order.remarks || '',
                        isActive: order.isActive
                    });

                } catch (error) {
                    Swal.fire('Error', 'Failed to load order', 'error');
                    navigate('/admin/orders');
                } finally {
                    setLoading(false);
                }
            };
            fetchOrder();
        } else {
            setLoading(false);
        }
    }, [id, isNew, navigate]);


    const validateField = (name, value) => {
        switch (name) {
            case 'productId': return value ? '' : 'Product is required';
            case 'fullName': return value.trim() ? '' : 'Name is required';
            case 'email': return value.trim() ? '' : 'Email is required';
            case 'contactNo': return value.trim() ? '' : 'Contact Number is required';
            case 'location': return value.trim() ? '' : 'Location is required';
            case 'remarks': return value.trim() ? '' : 'Remarks is required';
            default: return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
    };

    const handleImageSelect = (file) => {
        if (!file) return;

        if (previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        const objectUrl = URL.createObjectURL(file);
        setFormData(prev => ({ ...prev, image: file }));
        setPreviewUrl(objectUrl);
        setErrors(prev => ({ ...prev, image: '' }));
    };

    const handleSubmit = async () => {
        setTouched({ productId: true,fullName:true, email: true,contactNo: true, quantity: true });
        if (!formData.productId || !formData.fullName.trim()) {
            setErrors({
                productId: !formData.productId ? 'Product is required' : '',
                fullName: !formData.fullName.trim() ? 'Name is required' : '',
                email: !formData.email.trim() ? 'Email is required' : '',
                contactNo: !formData.contactNo.trim() ? 'Contact Number is required' : '',
                location: !formData.location.trim() ? 'Location is required' : '',
                remarks: !formData.remarks.trim() ? 'Remarks is required' : '',
            });
            return;
        }
        const confirm = await Swal.fire({
            title: 'Confirm',
            text: `Are you sure you want to update this product?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR
        });

        if (!confirm.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('productId', formData.productId);
            formDataToSend.append('fullName', formData.fullName || '');
            formDataToSend.append('email', formData.email || '');
            formDataToSend.append('contactNo', formData.contactNo);
            formDataToSend.append('location', formData.location);
            formDataToSend.append('remarks', formData.remarks);
            formDataToSend.append('isActive', formData.isActive);

            if (isNew) {
                await orderApi.create(formDataToSend);
            } else {
                await orderApi.update(id, formDataToSend);
            }

            Swal.fire('Success', `Order ${isNew ? 'added' : 'updated'}`, 'success');
            navigate('/admin/orders');
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.response?.data?.error || 'Operation failed', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <LinearProgress />;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Dialog open fullWidth maxWidth="md" onClose={() => navigate('/admin/orders')}>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between">
                        <Breadcrumbs>
                            <Link href="/admin" underline="hover" color="inherit">
                                <Home sx={{ mr: 0.5 }} /> Product Catalog
                            </Link>
                            <Link href="/admin/orders" underline="hover" color="inherit">
                                Orders
                            </Link>
                            <Typography color="text.primary">
                                {isNew ? 'Create' : 'Edit'}
                            </Typography>
                        </Breadcrumbs>
                    </Stack>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h5" gutterBottom>Order Information</Typography>
                            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                                <InputLabel>Product</InputLabel>
                                <Select
                                    name="productId"
                                    value={formData.productId}
                                    onChange={handleChange}
                                    label="Product"
                                >
                                    {products.map((prod) => (
                                        <MenuItem key={prod.id} value={prod.id}>
                                            {prod.prodName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                required
                                fullWidth
                                label="Name"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.fullName && Boolean(errors.fullName)}
                                helperText={touched.fullName && errors.fullName}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                required
                                fullWidth
                                label="Contact Number"
                                name="contactNo"
                                value={formData.contactNo}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                error={touched.contactNo && Boolean(errors.contactNo)}
                                helperText={touched.contactNo && errors.contactNo}
                                sx={{ mb: 3 }}
                                size="small"
                            />
                            <TextField
                                fullWidth
                                label="Location"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <TextField
                                fullWidth
                                label="Remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleInputChange}
                                sx={{ mb: 3 }}
                                size="small"
                            />

                            <FormControlLabel
                                control={<Switch checked={formData.isActive} onChange={(e) =>
                                    setFormData(prev => ({ ...prev, isActive: e.target.checked }))
                                } />}
                                label="Active"
                            />
                        </Grid>

                    </Grid>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ p: 3 }}>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/admin/orders')}
                            sx={{ background: BUTTON_CANCEL_COLOR }}
                        >
                            Cancel
                        </Button>
                    </AnimateButton>
                    <AnimateButton>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{ background: BUTTON_SAVE_COLOR }}
                        >
                            {isSubmitting ? 'Saving...' : isNew ? 'Create' : 'Save'}
                        </Button>
                    </AnimateButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrderBody;